import React, { forwardRef, useState } from 'react'
import { TextInput, View } from 'react-native'
import { twMerge } from 'tailwind-merge'

import { typographyVariants, variantForStyleId } from '../../typography/variants'
import { AdditionalText } from '../additionaltext/AdditionalText'
import { Label } from '../label/Label'
import { textareainputContract } from './textareainput.contract'

const VARIANTS = textareainputContract.props.variant.values

export type TextAreaInputVariant = (typeof VARIANTS)[number]

export const TEXT_AREA_INPUT_TEXT = variantForStyleId(textareainputContract.textStyleIds[0])

export type TextAreaInputProps = Omit<
    React.ComponentProps<typeof TextInput>,
    'style' | 'multiline' | 'className'
> & {
    /**
     * Figma's own five: `not-filled` and `filled` differ only in the ink, and
     * `focus`, `error` and `disabled` are the states the file draws. Hover is
     * not among them — the contract folds it into `hover:` classes, because a
     * web component should not be told when the pointer is over it. Focus is
     * tracked the same way: a `filled` or `not-filled` field draws `focus`
     * while it holds the caret.
     */
    variant?: TextAreaInputVariant
    /** Figma's `showLabel`, which toggles the `Label` this component places. */
    label?: string
    required?: boolean
    /** Figma's `showAdditionalText`, the hint or error under the field. */
    additionalText?: string
    className?: string
    testID?: string
}

type TextAreaRef = React.ComponentRef<typeof TextInput>

const variantFor = (variant: TextAreaInputVariant) => {
    const key = `variant=${variant}`
    const found = (
        textareainputContract.variants as Record<
            string,
            { container: string; box: string; label: string; disabled: string }
        >
    )[key]
    if (!found) {
        throw new Error(`aos-ds TextAreaInput: the Figma contract has no "${key}"`)
    }
    return found
}

/**
 * A labelled multi-line field: a `Label`, the field itself, and an
 * `AdditionalText` under it — all three are components this package already
 * ships, and `textareainputContract.composes` names them.
 *
 * The two class sets are not interchangeable. Figma paints the field on an
 * `input` child inside a stack that also holds the label and the hint, so
 * `container` is the stack — its gap, its height, its cross-axis alignment —
 * and `box` is the surface: fill, border, padding, corners. Putting both on one
 * element renders a 146px-tall field with the label inside it, which is what the
 * contract said before the emitter learned to tell them apart.
 *
 * The height Figma draws is the sample frame; a caller passes its own through
 * `className`, as `ListItem` and `ModalHeader` do.
 */
export const TextAreaInput = forwardRef<TextAreaRef, TextAreaInputProps>(
    (
        {
            variant: restingVariant = 'not-filled',
            label,
            required,
            additionalText,
            className,
            testID,
            onFocus,
            onBlur,
            ...rest
        },
        ref,
    ) => {
        const [focused, setFocused] = useState(false)
        const variant =
            focused && (restingVariant === 'filled' || restingVariant === 'not-filled')
                ? 'focus'
                : restingVariant
        const cell = variantFor(variant)
        const disabled = variant === 'disabled'
        return (
            <View
                className={twMerge(
                    'flex-col',
                    cell.container,
                    disabled ? cell.disabled : undefined,
                    className,
                )}
                testID={testID ?? `textarea-${variant}`}
            >
                {label ? <Label required={required}>{label}</Label> : null}
                <View className={twMerge('w-full flex-1', cell.box)} testID='textarea-box'>
                    <TextInput
                        ref={ref}
                        multiline
                        editable={!disabled}
                        className={twMerge(
                            'flex-1',
                            typographyVariants[TEXT_AREA_INPUT_TEXT],
                            cell.label,
                        )}
                        testID='textarea-field'
                        onFocus={event => {
                            setFocused(true)
                            onFocus?.(event)
                        }}
                        onBlur={event => {
                            setFocused(false)
                            onBlur?.(event)
                        }}
                        {...rest}
                    />
                </View>
                {additionalText ? (
                    <AdditionalText variant={variant === 'error' ? 'error' : 'default'}>
                        {additionalText}
                    </AdditionalText>
                ) : null}
            </View>
        )
    },
)

TextAreaInput.displayName = 'TextAreaInput'

export { VARIANTS as TEXT_AREA_INPUT_VARIANTS }
